import { API_URL } from '../../../constants/urls.constants';
import { getErrorMessage } from '../../../lib/error';
import { toast } from '../../../lib/toast';
import type { GetQueryArgsType } from '../../../types/global.types';
import { appServiceApi } from '../../apis/app-service.api';
import { APP_API_TAGS } from '../../app.constants';
import { userLoggedOut } from '../auth/auth.slice';
import type {
  AllIndustryParams,
  CreateOrganizationParams,
  CreateSubscriptionParams,
  CreateSubscriptionResponse,
  GetIndustriesResponse,
  GetPlansResponse,
  OrganizationResponse,
  ProceedToPaymentParams,
  ProceedToPaymentResponse,
  SeatUsageResponse,
  SubscriptionByAdminIdResponse,
  SubscriptionVerificationResponse,
  UpdateOrganizationParams,
} from './onboarding.type';

const { INDUSTRIES, ORGANIZATION, PLANS, SUBSCRIPTIONS } = APP_API_TAGS || {};

export const onboardingApi = appServiceApi.injectEndpoints({
  endpoints: (builder) => ({
    // ------ Start: API Endpoints for Organization Management ------
    createOrganization: builder.mutation({
      query: (data: CreateOrganizationParams) => ({
        url: API_URL.organizations,
        method: 'POST',
        body: data,
      }),
      // async onQueryStarted(_, { queryFulfilled, dispatch }) {
      //   try {
      //     const { data } = await queryFulfilled;
      //     const organization = data?.data;

      //     if (data?.success && organization) {
      //       // Update onboarding state on successful creation
      //       dispatch(updateStatus('created'));
      //       dispatch(setOrganizationInfo(organization));
      //     }
      //   } catch (err) {
      //     // Error handling is done in the component
      //   }
      // },
      invalidatesTags: [],
    }),

    updateOrganization: builder.mutation({
      query: ({ id, ...data }: UpdateOrganizationParams) => ({
        url: `${API_URL.organizations}/${id}`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: [ORGANIZATION],
    }),

    getSingleOrganization: builder.query<OrganizationResponse, string>({
      query: (id) => ({
        url: `${API_URL.organizations}/${id}`,
        method: 'GET',
      }),
      providesTags: [ORGANIZATION],
    }),

    getOrganizationByAdminId: builder.query<OrganizationResponse, string>({
      query: (id) => ({
        url: `/hrx/organizations/by-id/${id}`,
        method: 'GET',
      }),
      providesTags: [ORGANIZATION],
    }),
    // ------ END: API Endpoints for Organization Management ------

    // ------ Start: API Endpoints for Industry Management ------
    getAllIndustries: builder.query<
      GetIndustriesResponse,
      GetQueryArgsType<AllIndustryParams>
    >({
      query: (queryArgs) => ({
        url: API_URL.industries,
        method: 'GET',
        ...queryArgs,
      }),
      providesTags: [INDUSTRIES],
    }),

    getAllPlans: builder.query<
      GetPlansResponse,
      GetQueryArgsType<{ is_monthly?: boolean; is_yearly?: boolean }>
    >({
      query: (queryArgs) => ({
        url: API_URL.plans,
        method: 'GET',
        ...queryArgs,
      }),
      providesTags: [PLANS],
    }),

    createSubscription: builder.mutation<
      CreateSubscriptionResponse,
      CreateSubscriptionParams
    >({
      query: (data) => ({
        url: API_URL.subscriptions,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: [SUBSCRIPTIONS],
    }),

    verifySubscription: builder.query<SubscriptionVerificationResponse, string>(
      {
        query: (adminId) => ({
          url: `${API_URL.subscriptionVerification}/${adminId}`,
          method: 'GET',
        }),
        async onQueryStarted(_, { queryFulfilled, dispatch }) {
          try {
            await queryFulfilled;
          } catch (err) {
            dispatch(userLoggedOut());
          }
        },
        providesTags: [{ type: SUBSCRIPTIONS, id: 'verification' }],
      },
    ),

    getSeatUsage: builder.query<SeatUsageResponse, void>({
      query: () => ({ url: `${API_URL.subscriptions}/seat-usage` }),
      providesTags: [{ type: SUBSCRIPTIONS, id: 'seat-usage' }],
    }),

    getSubscriptionByAdminId: builder.query<
      SubscriptionByAdminIdResponse,
      string
    >({
      query: (adminId) => ({
        url: `${API_URL.subscriptionByAdminId}/${adminId}`,
        method: 'GET',
      }),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          if (data?.data?.length) {
            // const firstSubscription = data.data[0];
            // const planModules =
            //   firstSubscription?.plan_details?.planModules || [];
            // const modulesInfo = planModules.map((pm: any) => ({
            //   moduleId: pm.module_id,
            //   moduleName: pm.module?.name || 'N/A',
            // }));
            // dispatch(setModulesInfo(modulesInfo));
          }
        } catch (error) {
          localStorage.clear();
          dispatch(userLoggedOut());
          toast('error', getErrorMessage(error, 'Subscription not found.'));
        }
      },
      providesTags: [SUBSCRIPTIONS],
    }),
    proceedToPayment: builder.mutation<
      ProceedToPaymentResponse,
      ProceedToPaymentParams
    >({
      query: (data) => ({
        url: API_URL.proceedToPayment,
        method: 'POST',
        body: data,
      }),
    }),
    // ------ END: API Endpoints for Industry Management ------
  }),
});

// Export onboarding API hooks
export const {
  // Organization Management
  useCreateOrganizationMutation,
  useUpdateOrganizationMutation,
  useGetSingleOrganizationQuery,
  useGetOrganizationByAdminIdQuery,

  // Industry Management
  useGetAllIndustriesQuery,

  // Plan Management
  useGetAllPlansQuery,

  // Subscription Management
  useCreateSubscriptionMutation,
  useGetSubscriptionByAdminIdQuery,
  useGetSeatUsageQuery,
  useVerifySubscriptionQuery,
  useProceedToPaymentMutation,
} = onboardingApi;
